// One vendor, everything about them (F-FE-N93).
//
// Before this, answering "what's going on with Sana's Bakes?" meant three
// tabs: Purchasing for their contact details, Vendors / AP for what we owe,
// Vendor Reports for their payout — and no way to get from one to the next.
// Same endpoints, one page.
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { VendorCatalogSection } from './purchasing/VendorCatalogSection'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  Phone, Mail, MessageCircle, FileText, Receipt, RefreshCw, CheckCircle2, Pencil, Trash2, Plus,
} from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import { getStoredUserRole } from '@/lib/staff-roles'
import { billTitle, voidBlockedReason } from './vendorOpeningBalance'
import type {
  Supplier, VendorBill, VendorLedgerEntry, VendorSalesReport, VendorStockReport, PurchaseOrderSummary,
} from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Sheet, SheetContent, SheetHeader, SheetBody, SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { vendorStatusFromBills, isOverdue, isUnpaid, type VendorTone, vendorNetOwed, describeNetOwed } from './vendorStatus'

type ToastFn = (type: 'success' | 'error', message: string) => void

const TONE_CLASS: Record<VendorTone, string> = {
  danger: 'bg-danger-surface text-danger-fg border-danger-border',
  warning: 'bg-warning-surface text-warning-fg border-warning-border',
  success: 'bg-success-surface text-success-fg border-success-border',
  info: 'bg-info-surface text-info-fg border-info-border',
  neutral: 'bg-neutral-surface text-neutral-fg border-neutral-border',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2026-08-15" → "15 Aug". Falls back to the raw string if it doesn't parse. */
function shortDate(iso?: string | null): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`
}

function monthToDate(): { from: string; to: string } {
  const now = new Date()
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) }
}

function Tile({ label, value, sub, alarm }: { label: string; value: string; sub?: string; alarm?: boolean }) {
  return (
    <div className={cn(
      'flex flex-col gap-1 rounded-md border bg-background/80 px-3 py-2.5 shadow-sm',
      alarm && 'border-danger-border bg-danger-surface',
    )}>
      <span className={cn('text-[10px] font-medium uppercase tracking-wider', alarm ? 'text-danger-fg/85' : 'text-muted-foreground')}>
        {label}
      </span>
      <span className={cn('text-sm font-semibold tabular-nums', alarm && 'text-danger-fg')}>{value}</span>
      {sub && <span className={cn('text-xs', alarm ? 'text-danger-fg/90' : 'text-muted-foreground')}>{sub}</span>}
    </div>
  )
}

function SectionHead({ title, hint, children }: { title: string; hint?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-center gap-2">
      <h3 className="text-[15px] font-semibold">{title}</h3>
      {hint && (
        <span
          title={hint}
          className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border text-[10px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          ?
        </span>
      )}
      {children && <div className="ml-auto flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  )
}

interface FeedEvent {
  date: string
  line: string
  meta: string
  amount: number | null
  /** When set, the line links into the order's detail sheet on Purchasing. */
  poId?: string
}

export function VendorProfileSheet({
  vendor, open, onClose, onEdit, onDelete, showToast,
}: {
  vendor: Supplier | null
  open: boolean
  onClose: () => void
  onEdit: (s: Supplier) => void
  onDelete: (s: Supplier) => void
  showToast: ToastFn
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const isAdmin = getStoredUserRole() === 'admin'
  const navigate = useNavigate()
  const id = vendor?.id ?? ''
  const enabled = open && !!id

  const [view, setView] = useState<'simple' | 'accounting'>('simple')
  const [period, setPeriod] = useState(monthToDate)

  const { data: billsRes } = useQuery({
    queryKey: ['vendorBills', id],
    queryFn: () => apiClient.listVendorBills({ supplier_id: id }),
    enabled,
  })
  const bills: VendorBill[] = billsRes?.data ?? []

  const { data: ledgerRes } = useQuery({
    queryKey: ['vendorLedger', id],
    queryFn: () => apiClient.getVendorLedger(id),
    enabled,
  })
  const ledger: VendorLedgerEntry[] = ledgerRes?.data ?? []

  // Server-filtered by vendor (F-FE-N97): the old query fetched the newest 50
  // POs across ALL vendors and filtered client-side, so a vendor whose orders
  // fell outside that window showed an incomplete profile.
  const { data: poRes } = useQuery({
    queryKey: ['purchaseOrders', { supplier_id: id }],
    queryFn: () => apiClient.listPurchaseOrders({ page: 1, per_page: 50, supplier_id: id }),
    enabled,
  })
  const vendorPos: PurchaseOrderSummary[] = poRes?.data ?? []

  const salesQuery = useQuery({
    queryKey: ['vendor-sales', id, period.from, period.to],
    queryFn: () => apiClient.getVendorSalesReport(id, period.from, period.to).then(r => r.data),
    enabled: enabled && !!vendor?.is_consignment,
  })
  const sales: VendorSalesReport | undefined = salesQuery.data

  const { data: stockData } = useQuery({
    queryKey: ['vendor-stock', id],
    queryFn: () => apiClient.getVendorStockReport(id).then(r => r.data),
    enabled,
  })
  const stock: VendorStockReport | undefined = stockData

  const status = vendorStatusFromBills(bills)
  const unpaidBills = bills.filter(b => isUnpaid(b) || b.status === 'draft')
  // F-MM-N21: open bills net of unused supplier credits — the same number the
  // Accounting ledger below closes on. Negative means they owe you.
  const owed = vendorNetOwed(bills, vendor?.credit_balance)
  const overdue = bills.filter(isOverdue).reduce((s, b) => s + b.outstanding, 0)

  // Month-to-date money movement, read off the ledger the profile already has.
  const thisMonth = useMemo(() => {
    const prefix = period.from.slice(0, 7)
    let paid = 0
    let billed = 0
    for (const e of ledger) {
      if (!e.date.startsWith(prefix)) continue
      if (e.type === 'payment') paid += e.credit
      if (e.type === 'bill') billed += e.debit
    }
    return { paid, billed }
  }, [ledger, period.from])

  // ── Activity feed: the ledger's money events plus deliveries, told as
  // sentences. Same rows the Accounting view shows, read the other way. ────
  const feed: FeedEvent[] = useMemo(() => {
    const events: FeedEvent[] = ledger.map(e => {
      if (e.type === 'payment') {
        const method = /\(([^)]+)\)/.exec(e.description)?.[1]
        return {
          date: e.date,
          line: `You paid ${formatCurrency(e.credit)}`,
          meta: method === 'cash' ? 'Cash · posted to the till' : method ? `${method[0].toUpperCase()}${method.slice(1)} transfer` : 'Payment',
          amount: -e.credit,
        }
      }
      if (e.type === 'return') {
        const reason = e.description.replace(/^Return:\s*/, '').trim()
        return {
          date: e.date,
          line: 'Returned stock',
          meta: reason ? `${reason} — credited back to your account` : 'Credited back to your account',
          amount: -e.credit,
        }
      }
      return {
        date: e.date,
        line: e.description || 'Bill',
        meta: 'Added to what you owe',
        amount: e.debit,
      }
    })

    for (const po of vendorPos) {
      if (po.receipt_date) {
        events.push({
          date: po.receipt_date.slice(0, 10),
          line: 'Delivery received',
          meta: `${po.total_ordered_qty} item${po.total_ordered_qty === 1 ? '' : 's'} ordered`,
          amount: null,
          poId: po.id,
        })
      }
    }

    return events.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
  }, [ledger, vendorPos, formatCurrency])

  // ── Actions ──────────────────────────────────────────────────────────────
  const [payBill, setPayBill] = useState<VendorBill | null>(null)
  const [payAmount, setPayAmount] = useState('')
  const [payMethod, setPayMethod] = useState<'cash' | 'bank'>('cash')
  const [payNote, setPayNote] = useState('')
  // One stable idempotency key per open payment: a retry after a transient
  // error dedups server-side instead of paying twice.
  const payKey = useMemo(() => (payBill ? crypto.randomUUID() : ''), [payBill?.id])

  const [confirmBill, setConfirmBill] = useState<VendorBill | null>(null)
  // F-FE-N128: the confirm-invoice dialog doubles as "Edit bill" for a bill
  // that is already payable — same fields, same endpoint.
  const openBillEditor = (b: VendorBill) => {
    setConfirmBill(b)
    setRecInvoice(b.invoice_number || '')
    setRecBillDate(b.bill_date || '')
    setRecDueDate(b.due_date || '')
    setRecAmount(String(b.amount))
  }
  const [voidBill, setVoidBill] = useState<VendorBill | null>(null)
  const [recInvoice, setRecInvoice] = useState('')
  const [recBillDate, setRecBillDate] = useState('')
  const [recDueDate, setRecDueDate] = useState('')
  const [recAmount, setRecAmount] = useState('')

  const [statementOpen, setStatementOpen] = useState(false)
  const [stmtFrom, setStmtFrom] = useState(period.from)
  const [stmtTo, setStmtTo] = useState(period.to)
  const [stmtBusy, setStmtBusy] = useState<'pdf' | 'xlsx' | 'wa' | null>(null)
  const [settling, setSettling] = useState(false)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['vendorBills'] })
    qc.invalidateQueries({ queryKey: ['vendorAging'] })
    qc.invalidateQueries({ queryKey: ['vendorLedger'] })
    qc.invalidateQueries({ queryKey: ['vendor-sales'] })
    // F-FE-N118: the purchasing queue shows bill_status / bill_outstanding per
    // order — paying or confirming a bill here left it stale until a reload.
    qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
  }

  const pay = useMutation({
    mutationFn: () => {
      if (!payBill) throw new Error('No bill')
      return apiClient.createVendorPayment({
        vendor_bill_id: payBill.id,
        amount: Number(payAmount),
        method: payMethod,
        note: payNote || undefined,
        idempotency_key: payKey || undefined,
      })
    },
    onSuccess: (res) => {
      if (!res.success) { showToast('error', res.error || res.message || 'Failed'); return }
      showToast('success', 'Payment recorded')
      invalidate()
      setPayBill(null); setPayAmount(''); setPayNote('')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const reconcile = useMutation({
    mutationFn: () => {
      if (!confirmBill) throw new Error('No bill')
      return apiClient.updateVendorBill(confirmBill.id, {
        invoice_number: recInvoice || undefined,
        bill_date: recBillDate || undefined,
        due_date: recDueDate || undefined,
        amount: recAmount ? Number(recAmount) : undefined,
      })
    },
    onSuccess: (res) => {
      if (!res.success) { showToast('error', res.error || res.message || 'Failed'); return }
      showToast('success', confirmBill?.status === 'draft' ? 'Bill confirmed' : 'Bill updated')
      invalidate()
      setConfirmBill(null)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  // F-FE-N128: void, never delete. The row stays for the audit trail; every
  // AP query, the ledger, statements, aging and the "payout bill made" check
  // already skip void bills, and a voided settlement frees its period.
  const voidMutation = useMutation({
    mutationFn: () => {
      if (!voidBill) throw new Error('No bill')
      return apiClient.updateVendorBill(voidBill.id, { void: true })
    },
    onSuccess: (res) => {
      if (!res.success) { showToast('error', res.error || res.message || 'Failed'); return }
      showToast('success', 'Bill voided')
      invalidate()
      setVoidBill(null)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const runStatement = async (kind: 'pdf' | 'xlsx' | 'wa') => {
    if (!id || !stmtFrom || !stmtTo) { showToast('error', 'Pick a from and to date first.'); return }
    setStmtBusy(kind)
    try {
      if (kind === 'xlsx') {
        await apiClient.exportVendorLedgerXlsx(id, stmtFrom, stmtTo)
      } else {
        await apiClient.exportVendorStatementPdf(id, stmtFrom, stmtTo)
        if (kind === 'wa') {
          const res = await apiClient.sendVendorStatementWhatsApp(id, stmtFrom, stmtTo)
          const url = res.data?.wa_url
          if (!url) { showToast('error', res.error || 'Could not build the WhatsApp link'); return }
          window.open(url, '_blank', 'noopener,noreferrer')
          showToast('success', 'Opening WhatsApp — attach the downloaded PDF before sending.')
        }
      }
    } catch (e) {
      const code = e instanceof ApiClientError ? e.code : undefined
      showToast('error', code === 'supplier_has_no_phone'
        ? 'This vendor has no phone number on file. Add one with Edit.'
        : 'Could not produce the statement. Please try again.')
    } finally {
      setStmtBusy(null)
    }
  }

  const createPayout = async () => {
    if (!id) return
    setSettling(true)
    try {
      const res = await apiClient.createVendorSettlement(id, period.from, period.to)
      const amt = res.data?.settlement?.amount ?? sales?.total_vendor_payout ?? 0
      showToast('success', res.data?.already_existed
        ? `Already created: ${formatCurrency(amt)}`
        : `Payout bill created: ${formatCurrency(amt)} — it's in Unpaid bills above.`)
      invalidate()
      await salesQuery.refetch()
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Failed to create the payout bill')
    } finally {
      setSettling(false)
    }
  }

  if (!vendor) return null

  const commission = sales?.commission_percent ?? vendor.commission_percent ?? 0
  const waHref = vendor.phone ? `https://wa.me/${vendor.phone.replace(/[^0-9]/g, '')}` : null

  return (
    <>
      <Sheet open={open} onOpenChange={o => !o && onClose()}>
        <SheetContent size="xl" className="p-0" aria-describedby={undefined}>
          <SheetHeader className="gap-3">
            <div className="flex flex-wrap items-start gap-3 pr-8">
              <SheetTitle className="text-2xl">{vendor.name}</SheetTitle>
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                {!!vendor.is_consignment && (
                  <Badge variant="info-surface">Consignment · you keep {commission}%</Badge>
                )}
                <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium', TONE_CLASS[status.tone])}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                  {status.text}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
              {vendor.phone ? (
                <>
                  <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{vendor.phone}</span>
                  {waHref && (
                    <a href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline">
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </a>
                  )}
                </>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-warning-border bg-warning-surface px-2.5 py-0.5 text-xs text-warning-fg">
                  No phone on file — add one to send statements
                </span>
              )}
              {vendor.contact_name && <span>{vendor.contact_name}</span>}
              {vendor.email && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{vendor.email}</span>}
            </div>

            <div className="flex flex-wrap gap-2">
              {isAdmin && unpaidBills.some(isUnpaid) && (
                <Button
                  size="sm"
                  onClick={() => {
                    const target = unpaidBills.find(isUnpaid)!
                    setPayBill(target); setPayAmount(String(target.outstanding)); setPayMethod('cash')
                  }}
                >
                  Record payment
                </Button>
              )}
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => { setStmtFrom(period.from); setStmtTo(period.to); setStatementOpen(true) }}>
                  <FileText className="mr-1.5 h-3.5 w-3.5" /> Send statement
                </Button>
              )}
              {/* Standing on a vendor, "order from them" used to be a dead
                  end — the link is now two-way (F-FE-N97). */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate({ to: '/admin/inventory', search: { tab: 'purchasing', orderFor: vendor.id }, replace: true })}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> New order
              </Button>
              <Button size="sm" variant="outline" onClick={() => onEdit(vendor)}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => { onClose(); onDelete(vendor) }}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
              </Button>
            </div>
          </SheetHeader>

          <SheetBody className="flex flex-col gap-6">
            {/* Money at a glance */}
            <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
              <Tile label="You owe" {...describeNetOwed(owed, formatCurrency)} />
              <Tile
                label="Overdue"
                value={overdue > 0.01 ? formatCurrency(overdue) : 'None'}
                sub={overdue > 0.01 ? status.text : 'nothing past due'}
                alarm={overdue > 0.01}
              />
              <Tile label="Paid this month" value={formatCurrency(thisMonth.paid)} />
              <Tile
                label={vendor.is_consignment ? 'Their sales, this period' : 'Billed this month'}
                value={formatCurrency(vendor.is_consignment ? (sales?.total_gross ?? 0) : thisMonth.billed)}
              />
            </div>

            {/* Unpaid bills */}
            {unpaidBills.length > 0 && (
              <section>
                <SectionHead title={`Unpaid bills (${unpaidBills.length})`} />
                <div className="space-y-2">
                  {unpaidBills.map(b => (
                    <div key={b.id} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-3.5 py-3">
                      <div className="min-w-[180px] flex-1">
                        <div className="text-sm font-semibold">{billTitle(b)}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {b.status === 'draft'
                            ? 'Drafted when the stock arrived — check it against their invoice'
                            : `Due ${shortDate(b.due_date)}${b.status === 'partially_paid' ? ' · partly paid' : ''}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold tabular-nums">{formatCurrency(b.outstanding)} left</div>
                        <div className="text-xs text-muted-foreground tabular-nums">of {formatCurrency(b.amount)}</div>
                      </div>
                      {/* F-FE-N117: PUT /store/vendor-bills/:id is storeAdmin (admin
                          only). This button rendered for every role, so an
                          inventory_manager — whom the receive flow steers here "to
                          confirm the bill" — got a guaranteed 403. */}
                      {b.status === 'draft' ? (
                        isAdmin ? (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => openBillEditor(b)}>
                            Confirm invoice
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">Awaiting admin confirmation</span>
                        )
                      ) : isAdmin ? (
                        <>
                          {/* F-FE-N128: edit the amount / dates of a payable bill. */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 px-0"
                            aria-label="Edit bill"
                            title="Edit bill"
                            onClick={() => openBillEditor(b)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            className="h-8"
                            onClick={() => { setPayBill(b); setPayAmount(String(b.outstanding)); setPayMethod('cash') }}
                          >
                            Record payment
                          </Button>
                        </>
                      ) : null}
                      {/* F-FE-N128: a bill generated by mistake (a settlement run
                          "just for checking") had no way off this list. Void it —
                          the backend refuses once anything has been paid on it. */}
                      {isAdmin && (() => {
                        const blocked = voidBlockedReason(b)
                        return (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 px-0 text-danger-fg hover:bg-danger-surface hover:text-danger-fg"
                            aria-label="Void bill"
                            title={blocked ?? 'Void this bill'}
                            disabled={!!blocked}
                            onClick={() => setVoidBill(b)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )
                      })()}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Activity */}
            <section>
              <SectionHead title="Activity">
                <div className="flex items-center gap-1 rounded-md bg-muted p-1">
                  {(['simple', 'accounting'] as const).map(v => (
                    <Button
                      key={v}
                      size="sm"
                      variant={view === v ? 'default' : 'ghost'}
                      className="h-7 px-3 text-xs capitalize"
                      aria-pressed={view === v}
                      onClick={() => setView(v)}
                    >
                      {v}
                    </Button>
                  ))}
                </div>
              </SectionHead>

              {feed.length === 0 ? (
                <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                  Nothing yet — deliveries, bills and payments for this vendor will appear here.
                </p>
              ) : view === 'simple' ? (
                <>
                  <div className="divide-y rounded-lg border">
                    {feed.map((f, i) => (
                      <div key={i} className="flex gap-3.5 px-3.5 py-2.5">
                        <div className="w-16 shrink-0 pt-0.5 text-xs text-muted-foreground">{shortDate(f.date)}</div>
                        <div className="min-w-0 flex-1">
                          {f.poId ? (
                            <Link
                              to="/admin/inventory"
                              search={{ tab: 'purchasing' as const, po: f.poId }}
                              className="text-sm hover:underline"
                            >
                              {f.line}
                            </Link>
                          ) : (
                            <div className="text-sm">{f.line}</div>
                          )}
                          <div className="mt-0.5 text-xs text-muted-foreground">{f.meta}</div>
                        </div>
                        {f.amount != null && (
                          <div className={cn(
                            'shrink-0 text-sm font-medium tabular-nums',
                            f.amount > 0 ? 'text-danger-fg' : 'text-success-fg',
                          )}>
                            {f.amount > 0 ? '+' : '−'}{formatCurrency(Math.abs(f.amount))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2.5 flex items-baseline justify-between rounded-lg bg-muted px-3.5 py-3 font-semibold">
                    <span>Balance owed today</span>
                    <span className="tabular-nums">
                      {owed > 0.01
                        ? formatCurrency(owed)
                        : owed < -0.01
                          ? `Nothing owed · ${formatCurrency(-owed)} credit with them`
                          : 'Nothing owed'}
                    </span>
                  </div>
                  <p className="mt-2 border-l-2 py-1.5 pl-3 text-xs text-muted-foreground">
                    Red increases what you owe them; green reduces it. Switch to Accounting for the
                    debit/credit ledger — same entries, the form your accountant expects.
                  </p>
                </>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full min-w-[620px] text-left text-sm">
                    <thead className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Date</th>
                        <th className="px-3 py-2 font-semibold">Type</th>
                        <th className="px-3 py-2 font-semibold">Description</th>
                        <th className="px-3 py-2 text-right font-semibold">Debit</th>
                        <th className="px-3 py-2 text-right font-semibold">Credit</th>
                        <th className="px-3 py-2 text-right font-semibold">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {ledger.map((e, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-sm">{e.date}</td>
                          <td className="px-3 py-2 text-sm capitalize text-muted-foreground">{e.type}</td>
                          <td className="px-3 py-2 text-sm">{e.description}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{e.debit ? formatCurrency(e.debit) : '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{e.credit ? formatCurrency(e.credit) : '—'}</td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(e.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Consignment sales & payout — absent entirely for vendors you buy from */}
            {vendor.is_consignment && (
              <section>
                <SectionHead
                  title="Sales & payout"
                  hint="You stock their goods without buying them. When an item sells, they get their share and you keep your commission."
                >
                  <div className="flex items-center gap-1.5">
                    <DatePicker className="h-8 w-[140px] text-xs" value={period.from} onChange={v => setPeriod(p => ({ ...p, from: v }))} />
                    <span className="text-xs text-muted-foreground">to</span>
                    <DatePicker className="h-8 w-[140px] text-xs" value={period.to} onChange={v => setPeriod(p => ({ ...p, to: v }))} />
                  </div>
                  {sales?.settlement ? (
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-success-border bg-success-surface px-2.5 py-1 text-xs font-medium text-success-fg">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Payout bill made · {formatCurrency(sales.settlement.amount)}
                    </span>
                  ) : (sales?.total_vendor_payout ?? 0) > 0 ? (
                    <Button size="sm" className="h-8" disabled={settling} onClick={createPayout}>
                      {settling ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Receipt className="mr-1.5 h-3.5 w-3.5" />}
                      Create payout bill
                    </Button>
                  ) : null}
                </SectionHead>

                {salesQuery.isLoading ? (
                  <p className="rounded-lg border px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>
                ) : !sales || sales.items.length === 0 ? (
                  <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                    None of their items sold in this period.
                  </p>
                ) : (
                  <>
                    <div className="overflow-x-auto rounded-lg border">
                      <table className="w-full min-w-[620px] text-left text-sm">
                        <thead className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="px-3 py-2 font-semibold">Item</th>
                            <th className="px-3 py-2 text-right font-semibold">Sold</th>
                            <th className="px-3 py-2 text-right font-semibold">Customers paid</th>
                            <th className="px-3 py-2 text-right font-semibold">Your {commission}%</th>
                            <th className="px-3 py-2 text-right font-semibold">They get</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {sales.items.map((r, i) => (
                            <tr key={i}>
                              <td className="px-3 py-2">{r.item_name}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{r.qty_sold.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.gross_sales)}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatCurrency(r.house_cut)}</td>
                              <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(r.vendor_payout)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t bg-muted/30 font-semibold">
                          <tr>
                            <td className="px-3 py-2">Total</td>
                            <td className="px-3 py-2" />
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(sales.total_gross)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(sales.total_house_cut)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(sales.total_vendor_payout)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <p className="mt-2 border-l-2 py-1.5 pl-3 text-xs text-muted-foreground">
                      Counted over business days, so these tie out to the sales reports. Creating the payout
                      bill adds it to Unpaid bills above — nothing to go and find on another screen.
                      <Button variant="link" className="h-auto p-0 pl-1 text-xs" onClick={() => apiClient.exportVendorReportXlsx('sales', id, period.from, period.to)}>
                        Export to Excel
                      </Button>
                    </p>
                  </>
                )}

                {/* Ingredient usage: no revenue column on purpose — a dish built
                    from three suppliers has no true per-supplier revenue split. */}
                {(sales?.composed_usage?.length ?? 0) > 0 && (
                  <div className="mt-4">
                    <h4 className="mb-2 text-sm font-semibold">Also used as ingredients</h4>
                    <div className="overflow-x-auto rounded-lg border">
                      <table className="w-full min-w-[520px] text-left text-sm">
                        <thead className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="px-3 py-2 font-semibold">Item</th>
                            <th className="px-3 py-2 text-right font-semibold">Used</th>
                            <th className="px-3 py-2 font-semibold">In</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {sales!.composed_usage!.map((u, i) => (
                            <tr key={i}>
                              <td className="px-3 py-2">{u.item_name}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {Math.round(u.qty_consumed * 100000) / 100000} {u.unit}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{u.used_in}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-2 border-l-2 py-1.5 pl-3 text-xs text-muted-foreground">
                      Their stock used inside dishes that also use other vendors&apos; items. No revenue is
                      credited here — a plate built from three vendors can&apos;t be split between them.
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* What you buy from them */}
            <section>
              <SectionHead title={`What you buy from them${stock ? ` (${stock.items.length} item${stock.items.length === 1 ? '' : 's'})` : ''}`} />
              {!stock || stock.items.length === 0 ? (
                <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                  No inventory items are linked to this vendor yet. Set the vendor on a stock item and it will show up here.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Item</th>
                        <th className="px-3 py-2 font-semibold">Unit</th>
                        <th className="px-3 py-2 text-right font-semibold">On hand</th>
                        <th className="px-3 py-2 text-right font-semibold">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {stock.items.map((r, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2">
                            {r.item_name}
                            {r.oversold && (
                              <Badge variant="warning-surface" className="ml-2 text-[11px]">oversold — recount</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{r.unit}</td>
                          <td className={cn('px-3 py-2 text-right tabular-nums', r.oversold && 'text-warning-fg')}>
                            {r.qty_on_hand.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.stock_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t bg-muted/30 font-semibold">
                      <tr>
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2" />
                        <td className="px-3 py-2 text-right tabular-nums">{stock.total_qty.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(stock.total_value)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
            {/* Kitchen flow lane D — renders nothing with the flag off. */}
            <VendorCatalogSection supplierId={id} showToast={showToast} />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Record payment */}
      <Dialog open={!!payBill} onOpenChange={o => !o && setPayBill(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Record payment</DialogTitle>
            <DialogDescription>
              {vendor.name} · {payBill ? formatCurrency(payBill.outstanding) : ''} still owed on{' '}
              {payBill ? billTitle(payBill) : 'this bill'}. A cash payment comes out of the till.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input type="number" step="0.01" className="tabular-nums" value={payAmount} onChange={e => setPayAmount(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>How did you pay?</Label>
              <div className="flex gap-2">
                <Button type="button" variant={payMethod === 'cash' ? 'secondary' : 'outline'} className="h-10 text-sm" onClick={() => setPayMethod('cash')}>Cash</Button>
                <Button type="button" variant={payMethod === 'bank' ? 'secondary' : 'outline'} className="h-10 text-sm" onClick={() => setPayMethod('bank')}>Bank</Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input value={payNote} onChange={e => setPayNote(e.target.value)} placeholder="Optional reference…" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPayBill(null)}>Cancel</Button>
            <Button
              disabled={pay.isPending || !payAmount || Number(payAmount) <= 0}
              onClick={() => pay.mutate()}
            >
              Record payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm invoice (was "Reconcile") — also "Edit bill" for a payable bill (F-FE-N128) */}
      <Dialog open={!!confirmBill} onOpenChange={o => !o && setConfirmBill(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">{confirmBill?.status === 'draft' ? 'Confirm invoice' : 'Edit bill'}</DialogTitle>
            <DialogDescription>
              {confirmBill?.status === 'draft' ? (
                <>
                  We drafted this bill when the stock arrived. Enter what {vendor.name}&apos;s invoice actually
                  says — that makes it payable.
                </>
              ) : (
                <>
                  Correct what this bill says — {confirmBill ? billTitle(confirmBill) : ''}. Payments already
                  recorded against it stay as they are.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Invoice number</Label>
              <Input value={recInvoice} onChange={e => setRecInvoice(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Invoice date</Label>
                <DatePicker value={recBillDate} onChange={setRecBillDate} />
              </div>
              <div className="space-y-2">
                <Label>Payment due</Label>
                <DatePicker value={recDueDate} onChange={setRecDueDate} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input type="number" step="0.01" className="tabular-nums" value={recAmount} onChange={e => setRecAmount(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmBill(null)}>Cancel</Button>
            <Button disabled={reconcile.isPending} onClick={() => reconcile.mutate()}>
              {confirmBill?.status === 'draft' ? 'Confirm' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void bill (F-FE-N128) */}
      <Dialog open={!!voidBill} onOpenChange={o => !o && setVoidBill(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Void this bill?</DialogTitle>
            <DialogDescription>
              {voidBill ? billTitle(voidBill) : ''} · {voidBill ? formatCurrency(voidBill.amount) : ''}. It leaves
              what you owe {vendor.name}, the ledger and every statement.
              {voidBill?.settlement_from ? ' The period can be settled again afterwards.' : ''}
              {' '}The record is kept, marked void. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setVoidBill(null)}>Keep it</Button>
            <Button variant="destructive" disabled={voidMutation.isPending} onClick={() => voidMutation.mutate()}>
              Void bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statement */}
      <Dialog open={statementOpen} onOpenChange={setStatementOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Send {vendor.name} a statement</DialogTitle>
            <DialogDescription>
              A branded summary of what you were billed, what you paid, and what&apos;s still owed for the period.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">From</Label>
              <DatePicker className="h-10 text-sm" value={stmtFrom} onChange={setStmtFrom} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">To</Label>
              <DatePicker className="h-10 text-sm" value={stmtTo} onChange={setStmtTo} />
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button disabled={stmtBusy !== null} onClick={() => runStatement('pdf')}>
              {stmtBusy === 'pdf' ? 'Preparing…' : 'Download branded PDF'}
            </Button>
            <Button variant="outline" disabled={stmtBusy !== null || !vendor.phone} onClick={() => runStatement('wa')}>
              <MessageCircle className="mr-1.5 h-4 w-4" />
              {stmtBusy === 'wa' ? 'Preparing…' : 'Send on WhatsApp'}
            </Button>
            <Button variant="outline" disabled={stmtBusy !== null} onClick={() => runStatement('xlsx')}>
              {stmtBusy === 'xlsx' ? 'Preparing…' : 'Excel'}
            </Button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {vendor.phone
              ? 'WhatsApp downloads the PDF first and opens the chat with a summary — attach the file before sending.'
              : 'Add a phone number with Edit to send this on WhatsApp.'}
          </p>
        </DialogContent>
      </Dialog>
    </>
  )
}
